/**
 * FeeScheduleDisclosure -- the posted arranger / platform fee schedule
 * (dynamics Section 14), disclosed pre-commit on every surface where a buyer
 * moves toward a binding commitment. Wraps GET /fees/schedule assessed at
 * the listing's arranged amount (the syndicated notional on offer).
 *
 * Extracted from the Marketplace DealBriefDrawer (LPM-8) so the
 * OfferingDetail IOI dialog renders the SAME disclosure -- one
 * implementation, consumed by both pre-commit surfaces, matching the
 * eligibility.ts label-map extraction precedent.
 *
 * A failed fetch renders a VISIBLE error with a retry action, never a bare
 * empty list: this is a compliance disclosure surface, and an empty list
 * under the "disclosed pre-commit" header would read as "no fees apply"
 * (review 2026-07-10, wf_5962c795-b4a). The query deliberately inherits the
 * app-level QueryClient retry default (transient errors retry with backoff;
 * auth statuses do not).
 */

import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { Button, Skeleton } from '@aaimplatform/ui';
import { loanSyndicationClient } from '../../api/client';
import { formatCurrencyFull } from './format';

export function useFeeSchedule(arrangedAmount: number | undefined) {
  return useQuery({
    queryKey: ['lsp-fee-schedule', arrangedAmount ?? null],
    queryFn: async () => {
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/fees/schedule',
        { params: { query: { arrangedAmount } } },
      );
      if (error) {
        throw new Error((error as { message?: string })?.message ?? 'Failed to load the fee schedule');
      }
      return data;
    },
    // Only fetch once a real listing is on screen -- without this guard a
    // closed drawer / not-yet-loaded offering (arrangedAmount undefined)
    // fires a wasted /fees/schedule request on every mount.
    enabled: arrangedAmount !== undefined,
    staleTime: 60 * 1000,
  });
}

export function FeeScheduleDisclosure({ arrangedAmount }: { arrangedAmount: number | undefined }) {
  const feeSchedule = useFeeSchedule(arrangedAmount);

  return (
    <div>
      <div className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
        Fee schedule (disclosed pre-commit)
      </div>
      {feeSchedule.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : feeSchedule.isError ? (
        <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
          <div className="flex-1 text-xs font-sans text-error">
            The fee schedule could not be loaded -- fee terms are not shown.
          </div>
          <Button variant="secondary" size="sm" onClick={() => void feeSchedule.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {(feeSchedule.data?.schedule ?? []).map((item, i) => (
            <li key={i} className="flex items-center justify-between font-sans text-xs text-secondary">
              <span>{item.feeType.replace(/_/g, ' ')} -- {item.payer} pays {item.recipient}</span>
              <span className="font-sans font-medium tabular-nums text-primary">
                {item.basis === 'FLAT' ? formatCurrencyFull(item.rate) : `${item.rate} bps`}
              </span>
            </li>
          ))}
          {feeSchedule.data?.totalFees !== undefined && (
            <li className="flex items-center justify-between border-t border-recessed pt-1.5 font-sans text-xs font-semibold text-primary">
              <span>Total, on {formatCurrencyFull(feeSchedule.data.arrangedAmount)} arranged</span>
              <span className="tabular-nums">{formatCurrencyFull(feeSchedule.data.totalFees)}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
